import type * as DialogPrimitive from '@radix-ui/react-dialog'
import type {
  ComponentPropsWithoutRef,
  FormEvent,
  FormHTMLAttributes,
  ReactNode,
} from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DIALOG_CLOSE_BUTTON_CLASS,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { CircleNotchIcon, XIcon } from '@phosphor-icons/react'

/**
 * The two dialog widths. Everything that is a form or a confirmation is `md`;
 * `lg` is for the handful of dialogs that lay out two columns or a table.
 * Anything in between drifts, which is what the launch audit found (five
 * widths for five dialogs that do the same job).
 */
export type DialogPatternSize = 'md' | 'lg'

const sizeClasses: Record<DialogPatternSize, string> = {
  md: 'sm:max-w-[480px]',
  lg: 'sm:max-w-[880px]',
}

export interface DialogPatternProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Usually a string; JSX is allowed for a badge or emphasis inside it. */
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  /** Tints the icon tile, e.g. `bg-emerald-500/10` for an approve dialog. */
  iconClassName?: string
  /** Omit for a description-only dialog (a confirmation with a custom footer). */
  children?: ReactNode
  footer?: ReactNode
  isLoading?: boolean
  onCancel?: () => void
  onSubmit?: ((e?: FormEvent) => void) | (() => void)
  submitLabel?: string
  cancelLabel?: string
  size?: DialogPatternSize
  className?: string
  hideCloseButton?: boolean
  contentClassName?: string
  form?: string
  isSubmitting?: boolean
  isForm?: boolean
  formProps?: FormHTMLAttributes<HTMLFormElement>
  wrapContentInCard?: boolean
  contentCardClassName?: string
  iconSize?: 'sm' | 'md' | 'lg'
  modal?: boolean
  /**
   * Escape hatch onto the Radix content node for dismissal and focus hooks
   * (`onEscapeKeyDown`, `onInteractOutside`, `onOpenAutoFocus`, ...). Not for
   * styling: `className` and `children` stay with the shell.
   */
  contentProps?: Omit<
    ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    'className' | 'children'
  >
}

/**
 * The one dialog shell. Title, width, footer height and dismiss behaviour all
 * live here so individual dialogs cannot drift apart again.
 *
 * The chrome is deliberately quiet: one 24px margin on every side, an icon
 * tile without a border, a borderless close glyph and a footer with no band
 * or rule above it. Anything the shell draws that is not a title, a control
 * or a button is noise the form has to compete with, so the shell draws
 * nothing else. A dialog that needs a divider between body and actions is a
 * dialog whose body is too long.
 *
 * Escape always closes: nothing in here intercepts the key, so Radix's own
 * dismiss layer handles it, and a nested popover or combobox takes the first
 * Escape only because it is the higher layer. Focus returns to whatever opened
 * the dialog (see DialogContent). A dialog that must protect unsaved work
 * should pass `onCancel` and put a DiscardChangesDialog behind it rather than
 * swallowing the key.
 */
export function DialogPattern({
  open,
  onOpenChange,
  title,
  description,
  icon,
  iconClassName,
  children,
  footer,
  isLoading = false,
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  size = 'md',
  className,
  hideCloseButton = false,
  contentClassName,
  form,
  isSubmitting = false,
  isForm = false,
  formProps,
  wrapContentInCard = false,
  contentCardClassName,
  iconSize = 'md',
  modal = true,
  contentProps,
}: DialogPatternProps) {
  const shouldRenderFooter = footer !== null
  const hasCustomFooter = footer !== undefined && footer !== null

  // Default cancel handler if none provided
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      onOpenChange(false)
    }
  }

  // Icon size classes
  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  }

  // Content wrapper
  const ContentWrapper = isForm ? 'form' : 'div'
  const contentWrapperProps = isForm
    ? {
        onSubmit: onSubmit,
        ...formProps,
      }
    : {}

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent
        className={cn(
          sizeClasses[size],
          'flex flex-col gap-0 overflow-hidden rounded-lg border bg-background p-0 shadow-lg',
          className,
        )}
        // The close control is drawn inside the header row below so it lines
        // up with the icon tile and title instead of floating in the corner.
        hideCloseButton
        {...contentProps}
      >
        <ContentWrapper
          {...contentWrapperProps}
          className="flex h-full min-h-0 flex-col"
        >
          {/* Header section - fixed */}
          <div
            className={cn(
              'flex flex-shrink-0 gap-3 px-6 pb-2 pt-6',
              children === undefined || children === null || children === false
                ? 'pb-3'
                : 'pb-2',
              // A lone title centres on the icon tile; a title with a
              // description hangs from the top so the pair stays together.
              description ? 'items-start' : 'items-center',
            )}
          >
            {icon && (
              <div
                className={cn(
                  'flex flex-shrink-0 items-center justify-center rounded-lg bg-primary/10',
                  iconSizeClasses[iconSize],
                  iconClassName,
                )}
              >
                {icon}
              </div>
            )}

            <DialogHeader className="min-w-0 flex-1 space-y-0.5 text-left">
              <DialogTitle className="text-base font-semibold leading-snug tracking-tight">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-[13px] leading-snug">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>

            {!hideCloseButton && (
              <DialogClose className={DIALOG_CLOSE_BUTTON_CLASS}>
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            )}
          </div>

          {/* Content section - scrollable. Skipped entirely when the dialog
              is title + description + actions, so no empty band renders. */}
          {children !== undefined &&
            children !== null &&
            children !== false && (
              <div
                className={cn(
                  'flex-1 overflow-y-auto px-6 py-3',
                  contentClassName,
                )}
              >
                {wrapContentInCard ? (
                  <div
                    className={cn(
                      'rounded-lg border bg-card p-4 sm:p-5',
                      contentCardClassName,
                    )}
                  >
                    {children}
                  </div>
                ) : (
                  children
                )}
              </div>
            )}

          {/* Footer section - fixed */}
          {shouldRenderFooter && (
            <DialogFooter className="mt-0 flex-shrink-0 px-6 pb-5 pt-3 sm:justify-end">
              {hasCustomFooter ? (
                footer
              ) : (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="text-sm"
                  >
                    {cancelLabel}
                  </Button>
                  {onSubmit && (
                    <Button
                      type={form ? 'submit' : 'button'}
                      size="sm"
                      onClick={form ? undefined : onSubmit}
                      disabled={isLoading || isSubmitting}
                      className="min-w-[100px] text-sm"
                      form={form}
                    >
                      {isLoading ? (
                        <>
                          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : isSubmitting ? (
                        <>
                          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        submitLabel
                      )}
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          )}
        </ContentWrapper>
      </DialogContent>
    </Dialog>
  )
}
